import React, { useState, useEffect, useCallback } from 'react';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import {
    BarChart3, Download, FileSpreadsheet, FileText, Fuel, Gauge,
    TrendingUp, AlertTriangle, Calendar, Filter, RefreshCw, Truck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS_PIE = ['#D4AF37', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#10b981'];
const TIPO_LABELS = {
    rendimiento_bajo: 'Rend. Bajo',
    rendimiento_alto: 'Rend. Alto',
    km_inconsistente: 'KM Inconsist.',
    carga_excesiva: 'Carga Excesiva',
    licencia_vencida: 'Lic. Vencida',
};

export default function DieselReportesPage() {
    const { hasPermission } = useDiesel();
    const [data, setData] = useState(null);
    const [vehiculos, setVehiculos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    // Filtros
    const hoy = new Date();
    const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
    const [fechaDesde, setFechaDesde] = useState(primerDiaMes);
    const [fechaHasta, setFechaHasta] = useState(hoy.toISOString().slice(0, 10));
    const [vehiculoId, setVehiculoId] = useState('all');

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    const loadVehiculos = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/diesel/vehiculos?estado=activo`, { headers });
            if (res.ok) setVehiculos(await res.json());
        } catch { /* ignore */ }
    }, []);

    const loadReporte = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/diesel/reportes?`;
            if (fechaDesde) url += `fecha_desde=${fechaDesde}&`;
            if (fechaHasta) url += `fecha_hasta=${fechaHasta}&`;
            if (vehiculoId && vehiculoId !== 'all') url += `vehiculo_id=${vehiculoId}&`;

            const res = await fetch(url, { headers });
            if (res.ok) setData(await res.json());
            else toast.error('Error al cargar reporte');
        } catch {
            toast.error('Error de conexión');
        } finally {
            setLoading(false);
        }
    }, [fechaDesde, fechaHasta, vehiculoId]);

    useEffect(() => { loadVehiculos(); }, [loadVehiculos]);
    useEffect(() => { loadReporte(); }, [loadReporte]);

    const handleExport = async (formato) => {
        setExporting(true);
        try {
            let url = `${API_URL}/api/diesel/reportes/exportar-${formato}?`;
            if (fechaDesde) url += `fecha_desde=${fechaDesde}&`;
            if (fechaHasta) url += `fecha_hasta=${fechaHasta}&`;
            if (vehiculoId && vehiculoId !== 'all') url += `vehiculo_id=${vehiculoId}&`;

            const res = await fetch(url, { headers });
            if (!res.ok) {
                toast.error('Error al exportar');
                return;
            }

            const blob = await res.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            const ext = formato === 'excel' ? 'xlsx' : 'csv';
            link.download = `diesel_reporte_${new Date().toISOString().slice(0, 10)}.${ext}`;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success(`Archivo ${ext.toUpperCase()} descargado`);
        } catch {
            toast.error('Error al descargar');
        } finally {
            setExporting(false);
        }
    };

    const renderCustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div className="bg-card border-2 border-border rounded-lg p-3 shadow-lg text-sm">
                <p className="font-bold mb-1">{label}</p>
                {payload.map((p, i) => (
                    <p key={i} style={{ color: p.color }}>
                        {p.name}: <span className="font-mono font-bold">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
                    </p>
                ))}
            </div>
        );
    };

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-3" data-testid="reportes-title">
                            <BarChart3 className="h-8 w-8" style={{ color: 'var(--diesel-primary)' }} />
                            Reportes
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Análisis de consumo y rendimiento de la flota
                        </p>
                    </div>
                    {hasPermission('exportar_reportes') && (
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => handleExport('excel')}
                                disabled={exporting}
                                data-testid="export-excel-btn"
                            >
                                <FileSpreadsheet className="h-4 w-4 mr-2" />
                                Excel
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => handleExport('csv')}
                                disabled={exporting}
                                data-testid="export-csv-btn"
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                CSV
                            </Button>
                        </div>
                    )}
                </div>

                {/* Filtros */}
                <Card className="border-2" data-testid="reportes-filtros">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1 space-y-1">
                                <Label className="text-xs uppercase text-muted-foreground">Desde</Label>
                                <Input
                                    type="date"
                                    value={fechaDesde}
                                    onChange={e => setFechaDesde(e.target.value)}
                                    data-testid="filtro-fecha-desde"
                                />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Label className="text-xs uppercase text-muted-foreground">Hasta</Label>
                                <Input
                                    type="date"
                                    value={fechaHasta}
                                    onChange={e => setFechaHasta(e.target.value)}
                                    data-testid="filtro-fecha-hasta"
                                />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Label className="text-xs uppercase text-muted-foreground">Vehículo</Label>
                                <Select value={vehiculoId} onValueChange={setVehiculoId}>
                                    <SelectTrigger data-testid="filtro-vehiculo">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos los vehículos</SelectItem>
                                        {vehiculos.map(v => (
                                            <SelectItem key={v.id} value={v.id}>{v.patente}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button variant="outline" onClick={loadReporte} data-testid="btn-actualizar">
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Actualizar
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
                    </div>
                ) : data ? (
                    <>
                        {/* KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Card className="border-2" data-testid="kpi-total-cargas">
                                <CardContent className="p-4 text-center">
                                    <Fuel className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                                    <p className="text-3xl font-black">{data.kpis.total_cargas}</p>
                                    <p className="text-xs text-muted-foreground uppercase">Total Cargas</p>
                                </CardContent>
                            </Card>
                            <Card className="border-2" data-testid="kpi-total-litros">
                                <CardContent className="p-4 text-center">
                                    <Fuel className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                                    <p className="text-3xl font-black">{data.kpis.total_litros.toLocaleString()}</p>
                                    <p className="text-xs text-muted-foreground uppercase">Litros</p>
                                </CardContent>
                            </Card>
                            <Card className="border-2" data-testid="kpi-total-km">
                                <CardContent className="p-4 text-center">
                                    <TrendingUp className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                                    <p className="text-3xl font-black">{data.kpis.total_km.toLocaleString()}</p>
                                    <p className="text-xs text-muted-foreground uppercase">KM Recorridos</p>
                                </CardContent>
                            </Card>
                            <Card className="border-2" data-testid="kpi-rendimiento">
                                <CardContent className="p-4 text-center">
                                    <Gauge className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                                    <p className="text-3xl font-black">
                                        {data.kpis.rendimiento_promedio ?? '-'}
                                    </p>
                                    <p className="text-xs text-muted-foreground uppercase">Rend. Prom (km/l)</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Gráficos */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Consumo Mensual */}
                            <Card className="border-2" data-testid="chart-consumo-mensual">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Fuel className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                        Consumo Mensual (Litros)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.consumo_mensual.length > 0 ? (
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data.consumo_mensual}>
                                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip content={renderCustomTooltip} />
                                                    <Bar dataKey="litros" fill="var(--diesel-primary)" name="Litros" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <p className="text-center py-8 text-muted-foreground">Sin datos en el período</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Rendimiento Mensual */}
                            <Card className="border-2" data-testid="chart-rendimiento-mensual">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Gauge className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                        Rendimiento Mensual (km/l)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.consumo_mensual.filter(m => m.rendimiento).length > 0 ? (
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={data.consumo_mensual.filter(m => m.rendimiento)}>
                                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip content={renderCustomTooltip} />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="rendimiento"
                                                        stroke="var(--diesel-primary)"
                                                        strokeWidth={3}
                                                        dot={{ r: 5 }}
                                                        name="Rendimiento"
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <p className="text-center py-8 text-muted-foreground">Sin datos de rendimiento</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Ranking Vehículos */}
                            <Card className="border-2" data-testid="chart-ranking">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Truck className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                        Ranking de Vehículos (Rendimiento)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.ranking_vehiculos.length > 0 ? (
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={data.ranking_vehiculos.slice(0, 10)} layout="vertical">
                                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                    <XAxis type="number" tick={{ fontSize: 11 }} />
                                                    <YAxis dataKey="patente" type="category" tick={{ fontSize: 11 }} width={80} />
                                                    <Tooltip content={renderCustomTooltip} />
                                                    <Legend />
                                                    <Bar dataKey="rendimiento" fill="var(--diesel-primary)" name="Rend. Real" radius={[0, 4, 4, 0]} />
                                                    <Bar dataKey="rendimiento_fabricante" fill="#ef4444" name="Rend. Fabricante" radius={[0, 4, 4, 0]} opacity={0.5} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <p className="text-center py-8 text-muted-foreground">Sin datos de rendimiento</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Alertas por Tipo */}
                            <Card className="border-2" data-testid="chart-alertas">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <AlertTriangle className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                        Distribución de Alertas
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.alertas_por_tipo.length > 0 ? (
                                        <div className="h-64 flex items-center">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={data.alertas_por_tipo.map(a => ({
                                                            ...a,
                                                            name: TIPO_LABELS[a.tipo] || a.tipo,
                                                        }))}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={50}
                                                        outerRadius={90}
                                                        dataKey="count"
                                                        nameKey="name"
                                                        label={({ name, count }) => `${name}: ${count}`}
                                                    >
                                                        {data.alertas_por_tipo.map((_, i) => (
                                                            <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <p className="text-center py-8 text-muted-foreground">Sin alertas en el período</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Tabla Ranking Detallado */}
                        {data.ranking_vehiculos.length > 0 && (
                            <Card className="border-2" data-testid="tabla-ranking">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <TrendingUp className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                        Detalle por Vehículo
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b bg-muted/50">
                                                    <th className="text-left p-3 font-medium">Patente</th>
                                                    <th className="text-right p-3 font-medium">Cargas</th>
                                                    <th className="text-right p-3 font-medium">Litros</th>
                                                    <th className="text-right p-3 font-medium">KM</th>
                                                    <th className="text-right p-3 font-medium">Rend. Real</th>
                                                    <th className="text-right p-3 font-medium">Rend. Fab.</th>
                                                    <th className="text-right p-3 font-medium">Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.ranking_vehiculos.map((v) => {
                                                    const diff = v.rendimiento_fabricante
                                                        ? ((v.rendimiento / v.rendimiento_fabricante) * 100 - 100).toFixed(1)
                                                        : null;
                                                    return (
                                                        <tr key={v.vehiculo_id} className="border-b hover:bg-muted/30 transition-colors">
                                                            <td className="p-3 font-mono font-bold">{v.patente}</td>
                                                            <td className="p-3 text-right">{v.cargas}</td>
                                                            <td className="p-3 text-right font-mono">{v.litros.toLocaleString()}</td>
                                                            <td className="p-3 text-right font-mono">{v.km.toLocaleString()}</td>
                                                            <td className="p-3 text-right font-mono font-bold">{v.rendimiento} km/l</td>
                                                            <td className="p-3 text-right font-mono text-muted-foreground">
                                                                {v.rendimiento_fabricante ? `${v.rendimiento_fabricante} km/l` : '-'}
                                                            </td>
                                                            <td className="p-3 text-right">
                                                                {diff !== null ? (
                                                                    <Badge className={parseFloat(diff) >= 0 ? 'bg-emerald-500' : 'bg-red-500'}>
                                                                        {parseFloat(diff) >= 0 ? '+' : ''}{diff}%
                                                                    </Badge>
                                                                ) : (
                                                                    <span className="text-muted-foreground">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </>
                ) : null}
            </div>
        </DieselLayout>
    );
}
